import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface DemoModeContextType {
  isDemoMode: boolean;
  setIsDemoMode: (enabled: boolean) => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

function getIsDemoUserFromToken(): boolean {
  try {
    const idToken = localStorage.getItem('zecure_cognito_id_token');
    if (!idToken) return false;
    const parts = idToken.split('.');
    if (parts.length !== 3) return false;
    const payloadJson = atob(parts[1]);
    const payload = JSON.parse(payloadJson);
    return payload['custom:isDemo'] === 'true' || payload['custom:isDemo'] === true;
  } catch {
    return false;
  }
}

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const [isDemoMode, setIsDemoMode] = useState<boolean>(() => {
    try {
      const isDemoUser = getIsDemoUserFromToken();
      // 1) Force ON for Cognito demo users.
      if (isDemoUser) return true;

      // 2) For non-demo users, always start with demo mode OFF (checkbox deselected),
      // regardless of any previously saved preference.
      localStorage.removeItem('demoMode');
      return false;
    } catch {
      // Ignore any parsing / localStorage errors and fall back to default
    }
    return false;
  });

  const handleSetDemoMode = (enabled: boolean) => {
    // Demo users must always stay in demo mode regardless of the Settings toggle.
    if (getIsDemoUserFromToken()) {
      setIsDemoMode(true);
      localStorage.setItem('demoMode', 'true');
      return;
    }
    setIsDemoMode(enabled);
    localStorage.setItem('demoMode', enabled.toString());
  };

  useEffect(() => {
    const syncDemoModeFromToken = () => {
      if (getIsDemoUserFromToken()) {
        setIsDemoMode(true);
        localStorage.setItem('demoMode', 'true');
      }
    };
    // Keep demo mode in sync after login/token refresh without needing a page refresh.
    syncDemoModeFromToken();
    window.addEventListener('focus', syncDemoModeFromToken);
    window.addEventListener('storage', syncDemoModeFromToken);
    return () => {
      window.removeEventListener('focus', syncDemoModeFromToken);
      window.removeEventListener('storage', syncDemoModeFromToken);
    };
  }, []);

  return (
    <DemoModeContext.Provider value={{ isDemoMode, setIsDemoMode: handleSetDemoMode }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}

